import { Box, Text } from "@dehub/theme";
import type { DataTableColumn } from "@dehub/theme";
import { FileTextIcon, PencilSimpleIcon } from "@phosphor-icons/react";
import type { TFunction } from "i18next";
import type { ReactNode } from "react";
import bz from "@/features/business/business.module.css";
import type { IBranchListRow } from "@/models/Branch";
import type { IReference } from "@/models/Reference";
import { RowAction } from "@/shared/a11y/row-action";
import { MissingPermission } from "@/shared/a11y/trans-code";
import { formatDateTime } from "@/utils/format-date";
import { renderBusinessName } from "@/utils/render-business-name";
import type { BranchPermissions } from "../permissions";
import type { RowActionKey } from "../types";
import classes from "../branch.module.css";
import { BranchLogo } from "./BranchLogo";

type Args = {
  perms: BranchPermissions;
  branchStatus: IReference[];
  branchTypes: IReference[];
  t: TFunction;
  tCommon: TFunction;
  onRowAction: (key: RowActionKey, row: IBranchListRow) => void;
  onLogoPreview: (src: string, name: string) => void;
};

export function createBranchColumns({
  perms,
  branchStatus,
  branchTypes,
  t,
  tCommon,
  onRowAction,
  onLogoPreview,
}: Args): DataTableColumn<IBranchListRow>[] {
  const missing = (...codes: string[]): ReactNode => <MissingPermission code={codes.join(", ")} />;

  const statusLabel = (code?: string | null) => {
    if (code) {
      const key = `status.${code}`;
      const translated = tCommon(key);
      if (translated !== key) return translated;
    }
    return branchStatus.find((r) => r?.code === code)?.name ?? code ?? "-";
  };

  return [
    {
      key: "actions",
      header: tCommon("col.action"),
      width: 110,
      render: (row) => {
        // Edit opens the drawer, which loads GET /prt/branch/:id (DETAIL) and saves with PUT (EDIT).
        const editMissing: string[] = [];
        if (!perms.edit.hasAccess) editMissing.push(perms.codes.edit);
        if (!perms.detail.hasAccess) editMissing.push(perms.codes.detail);
        const editReason: ReactNode = editMissing.length > 0 ? missing(...editMissing) : null;
        return (
          <Box className={bz.rowActions}>
            <RowAction
              label={tCommon("actions.edit")}
              reason={editReason}
              icon={PencilSimpleIcon}
              anchor={`${row.id}:edit`}
              onClick={() => onRowAction("edit", row)}
            />
            <RowAction
              label={tCommon("actions.detail")}
              reason={perms.detail.hasAccess ? null : missing(perms.codes.detail)}
              icon={FileTextIcon}
              anchor={`${row.id}:detail`}
              onClick={() => onRowAction("detail", row)}
            />
          </Box>
        );
      },
    },
    {
      key: "branch",
      header: t("col.branch"),
      render: (row) => (
        <Box className={bz.bizCell}>
          <BranchLogo logo={row.logo} name={row.name} nameEng={row.nameEng} onPreview={onLogoPreview} />
          <Box className={bz.bizText}>
            <Text component="span" className={bz.bizName}>
              {renderBusinessName({ profileName: row.name, profileNameEng: row.nameEng })}
            </Text>
            <Text component="span" className={bz.mono} translate="no">
              {row.refCode ?? "-"}
            </Text>
          </Box>
        </Box>
      ),
    },
    {
      key: "type",
      header: tCommon("col.type"),
      width: 170,
      render: (row) => (
        <Text component="span" className={bz.muted}>
          {branchTypes.find((item) => item.code === row.type)?.name ?? "-"}
        </Text>
      ),
    },
    {
      key: "roles",
      header: t("col.roles"),
      render: (row) => {
        const buyers = row.isBuyer ? (row.buyers ?? []) : [];
        const suppliers = row.isSupplier ? (row.suppliers ?? []) : [];
        const chips: { key: string; label: string; code?: string | null }[] = [
          ...(row.isBuyer ? (buyers.length > 0 ? buyers.map((b) => ({ key: `b-${b.id}`, label: t("roles.buyer"), code: b.refCode })) : [{ key: "b", label: t("roles.buyer") }]) : []),
          ...(row.isSupplier ? (suppliers.length > 0 ? suppliers.map((s) => ({ key: `s-${s.id}`, label: t("roles.supplier"), code: s.refCode })) : [{ key: "s", label: t("roles.supplier") }]) : []),
        ];
        if (!row.isDefault && chips.length === 0) return <Text component="span" className={bz.muted}>{t("roles.none")}</Text>;
        return (
          <Box className={classes.roleChips}>
            {row.isDefault ? (
              <Box component="span" className={classes.roleChip}>
                {t("roles.main")}
              </Box>
            ) : null}
            {chips.map((chip) => (
              <Box key={chip.key} component="span" className={classes.roleChip}>
                {chip.label}
                {chip.code ? (
                  <Text component="span" className={classes.roleChipCode} translate="no">
                    {chip.code}
                  </Text>
                ) : null}
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      key: "status",
      header: tCommon("col.status"),
      width: 210,
      render: (row) => (
        <Box className={bz.pills}>
          <Box component="span" className={`${bz.pill} ${row.branchStatus === "ACTIVE" ? bz.pillActive : ""}`}>
            <Box component="span" className={bz.pillDot} />
            {statusLabel(row.branchStatus)}
          </Box>
          {row.isConfirmed ? (
            <Box component="span" className={bz.pill}>
              {t("confirmed")}
            </Box>
          ) : null}
        </Box>
      ),
    },
    {
      key: "createdAt",
      header: t("col.created"),
      width: 160,
      render: (row) => (
        <Text component="span" className={bz.mono}>
          {formatDateTime(row.createdAt) || "-"}
        </Text>
      ),
    },
  ];
}
